import { useState, useRef, useEffect } from "react";
import { GoogleGenAI } from "@google/genai";

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

const SYSTEM_PROMPT = `You are Sprout, a cute and friendly AI assistant built into Leafsy — a web app that reduces food waste by connecting surplus food with people who need it. You speak in a warm, helpful, and concise tone. Keep answers short (2-4 sentences max) unless the user asks for detail. Use emoji sparingly but naturally.

Here is everything you know about Leafsy:

WHAT IS LEAFSY:
Leafsy is a hackathon project web app hosted at leafsy.xyz. It connects surplus food with people who need it. Donors post extra food, receivers claim it for pickup or delivery, and volunteer couriers deliver items. Everything shows up on an interactive map.

ROLES (tabs in the top navbar):
- Donor (green tab): Post surplus food with a name, quantity, hours left, perishable/non-perishable, a pickup address (with autocomplete), and an optional photo. Posts appear as green pins on the map.
- Receiver (blue tab): Browse available food in a list sorted by distance & urgency, or click green pins on the map. Claim items for pickup (go yourself) or request delivery (a courier brings it). You enter a delivery address when requesting delivery.
- Deliver (amber tab): See delivery requests awaiting a courier. Accept a delivery — it moves to My Claims. Track progress: Picked Up → In Transit → Mark Delivered.

MY CLAIMS (left panel, top-left button):
Shows all active pickups, delivery requests, and courier jobs. Pickups have a "Mark Picked Up" button. Courier deliveries have "Picked Up — In Transit" and "Mark Delivered" buttons. Click ✕ to cancel any claim — the item becomes available again.

MAP:
Full-screen interactive map (Leaflet + OpenStreetMap). Blue pin = user location. Green pins = available food. Orange pin = pickup point for active delivery. Red pin = drop-off destination. Dotted purple line = courier route. Click green pins to see details and claim.

SETTINGS (gear icon ⚙️ in navbar):
Set a home location by typing an address (used when GPS is off). View GPS status. Log out button is at the bottom.

PHOTOS:
Donors can optionally add a photo when posting. Photos are auto-compressed. Leafsy leaf logo is the placeholder.

AUTH:
Sign up with email/password or Google. Log out from Settings.

NOTIFICATIONS:
Receivers get real-time toast notifications when delivery status changes (courier accepted, in transit, delivered).

ADDRESS INPUT:
All address fields use autocomplete from OpenStreetMap/Nominatim. Start typing and select from suggestions.

FOOD SORTING:
Items sorted by combination of distance and urgency. Items < 3 hours left are marked "Urgent" with red badge.

AI FEATURES:
Leafsy uses a priority scoring algorithm combining distance and urgency to sort food listings.

TECH STACK:
React, Tailwind CSS, Firebase (Auth, Firestore, Storage), Leaflet maps, OpenStreetMap/Nominatim geocoding, Google Gemini AI (that's you!).

IMPORTANT RULES:
- Only answer questions related to Leafsy and its features.
- If someone asks about something unrelated, politely redirect them: "I'm Sprout, and I specialize in Leafsy! Ask me anything about how the app works."
- Never make up features that don't exist.
- Be concise — this is a chat widget, not an essay.
- Use **bold** for important terms (the chat renders markdown bold).`;

const TUTORIAL_STEPS_DESKTOP = [
  { text: "Welcome to **Leafsy**! 🌱 I'm Sprout, your guide. Let me show you around so you can start rescuing food right away!" },
  { text: "At the **top** you'll see **three tabs**: **Donor** (post extra food), **Receiver** (claim food), and **Deliver** (volunteer to deliver). Switch between them to do different things!" },
  { text: "The **search bar** next to Leafsy lets you search for any address — the map zooms right there. Perfect for finding pickup spots or your neighborhood!" },
  { text: "**My Claims** — the 📋 button at the **top left** — opens a side panel with your active pickups and deliveries. Mark things done, send kudos, or cancel from there." },
  { text: "**Green pins** on the map = available food. **Blue pin** = you. **Click** a green pin to see details and claim! You can pick up yourself or request delivery." },
  { text: "Hit the **Settings** gear ⚙️ in the top bar to set your home location (used when GPS is off) and manage your preferences." },
  { text: "That's it! We make it **Leafsy** to share — easy to use, easy on the earth. Tap me anytime for help. Happy rescuing! 🌿" },
];

const TUTORIAL_STEPS_MOBILE = [
  { text: "Welcome to **Leafsy**! 🌱 I'm Sprout, your guide. Let me show you around so you can start rescuing food right away!" },
  { text: "At the **top** you'll see **three tabs**: **Donor** (post extra food), **Receiver** (claim food), and **Deliver** (volunteer to deliver). Switch between them to do different things!" },
  { text: "The **search bar** in the top bar lets you search for any address — the map zooms right there. Perfect for finding pickup spots or your neighborhood!" },
  { text: "**My Claims** — the 📋 button at the **bottom right** — opens a sheet from the bottom with your active pickups and deliveries. Mark things done, send kudos, or cancel from there." },
  { text: "**Green pins** on the map = available food. **Blue pin** = you. **Tap** a green pin to see details and claim! You can pick up yourself or request delivery." },
  { text: "Tap the **Settings** gear ⚙️ in the top bar to set your home location (used when GPS is off) and manage your preferences." },
  { text: "That's it! We make it **Leafsy** to share — easy to use, easy on the earth. Tap me anytime for help. Happy rescuing! 🌿" },
];

let aiInstance = null;
function getAI() {
  if (!aiInstance && GEMINI_API_KEY) {
    aiInstance = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  }
  return aiInstance;
}

async function getAIResponse(userMessage, chatHistory) {
  const ai = getAI();
  if (!ai) {
    return "I need a Gemini API key to work! Ask the developer to set REACT_APP_GEMINI_API_KEY in the .env file. 🌱";
  }

  try {
    // Build conversation context
    const conversationContext = chatHistory
      .map(m => `${m.from === "user" ? "User" : "Sprout"}: ${m.text}`)
      .join("\n");

    const prompt = `${SYSTEM_PROMPT}\n\nConversation so far:\n${conversationContext}\nUser: ${userMessage}\nSprout:`;

    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: prompt,
    });

    return response.text || "Hmm, I couldn't think of a response. Try asking differently! 🌱";
  } catch (err) {
    console.error("Gemini API error:", err);
    if (err.message?.includes("API_KEY")) {
      return "My API key doesn't seem to be working. Please check the REACT_APP_GEMINI_API_KEY in the .env file.";
    }
    return "Oops, I had trouble thinking just now. Try again in a moment! 🌱";
  }
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

export default function SproutChat({ isOpen, onClose, tutorialMode = false, onTutorialComplete }) {
  const [tutorialStep, setTutorialStep] = useState(0);
  const [messages, setMessages] = useState([
    { from: "sprout", text: "Hey! I'm Sprout 🌱, your Leafsy guide. Ask me anything about how the app works!" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, tutorialStep]);

  if (!isOpen) return null;

  const tutorialSteps = isMobile ? TUTORIAL_STEPS_MOBILE : TUTORIAL_STEPS_DESKTOP;

  const handleTutorialNext = () => {
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(s => s + 1);
    } else {
      onTutorialComplete?.();
    }
  };

  if (tutorialMode) {
    const step = tutorialSteps[tutorialStep];
    const isLast = tutorialStep === tutorialSteps.length - 1;
    return (
      <div className="glass-hover fixed bottom-6 left-6 z-[2002] w-80 sm:w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 flex flex-col overflow-hidden" style={{ maxHeight: "min(400px, calc(100vh - 8rem))" }}>
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-200/50 flex-shrink-0">
          <img src="/sprout-icon.png" alt="Sprout" className="w-9 h-9 object-contain flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-condensed text-base font-bold text-gray-800 uppercase tracking-tight">Sprout's Tour</h3>
            <p className="text-[10px] text-green-600 font-medium">Step {tutorialStep + 1} of {tutorialSteps.length}</p>
          </div>
          <button onClick={onTutorialComplete} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 text-xs">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="flex items-start gap-2">
            <img src="/sprout-icon.png" alt="Sprout" className="w-8 h-8 object-contain flex-shrink-0 mt-0.5" />
            <div className="bg-gray-100 text-gray-700 px-4 py-3 rounded-2xl rounded-tl-md text-sm leading-relaxed">
              {step.text.split("**").map((part, j) => j % 2 === 1 ? <strong key={j} className="text-gray-900">{part}</strong> : <span key={j}>{part}</span>)}
            </div>
          </div>
        </div>
        <div className="px-4 pb-4 pt-2 flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {tutorialSteps.map((_, i) => (
              <div key={i} className={`w-1.5 h-1.5 rounded-full ${i <= tutorialStep ? "bg-green-500" : "bg-gray-200"}`} />
            ))}
          </div>
          <button onClick={handleTutorialNext} className="btn-pop px-4 py-2 bg-green-500 text-white text-sm font-semibold rounded-xl hover:bg-green-600">
            {isLast ? "Got it! 🌿" : "Next"}
          </button>
        </div>
      </div>
    );
  }

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    const newMessages = [...messages, { from: "user", text: userMsg }];
    setMessages(newMessages);
    setInput("");
    setLoading(true);

    const reply = await getAIResponse(userMsg, messages);
    setMessages(prev => [...prev, { from: "sprout", text: reply }]);
    setLoading(false);
  };

  const handleQuickAsk = async (q) => {
    if (loading) return;
    const newMessages = [...messages, { from: "user", text: q }];
    setMessages(newMessages);
    setLoading(true);

    const reply = await getAIResponse(q, messages);
    setMessages(prev => [...prev, { from: "sprout", text: reply }]);
    setLoading(false);
  };

  return (
    <div
      className="glass-hover fixed bottom-6 left-6 z-[1002] w-80 sm:w-96 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/50 flex flex-col overflow-hidden"
      style={{ maxHeight: "min(480px, calc(100vh - 8rem))" }}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-200/50 flex-shrink-0">
        <img src="/sprout-icon.png" alt="Sprout" className="w-9 h-9 object-contain flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-condensed text-base font-bold text-gray-800 uppercase tracking-tight leading-tight">Sprout</h3>
          <div className="flex items-center gap-1.5">
            <p className="text-[10px] text-green-600 font-medium">Leafsy Assistant</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-400 text-xs"
        >
          ✕
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {messages.map((msg, i) => (
          <div key={i} className={`flex items-end gap-1.5 ${msg.from === "user" ? "justify-end" : "justify-start"}`}>
            {msg.from === "sprout" && (
              <img src="/sprout-icon.png" alt="Sprout" className="w-6 h-6 object-contain flex-shrink-0 mb-1" />
            )}
            <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
              msg.from === "user"
                ? "bg-green-500 text-white rounded-br-md"
                : "bg-gray-100 text-gray-700 rounded-bl-md"
            }`}>
              {msg.text.split("**").map((part, j) =>
                j % 2 === 1
                  ? <strong key={j} className={msg.from === "user" ? "text-white" : "text-gray-900"}>{part}</strong>
                  : <span key={j}>{part}</span>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-end gap-1.5 justify-start">
            <img src="/sprout-icon.png" alt="Sprout" className="w-6 h-6 object-contain flex-shrink-0 mb-1" />
            <div className="bg-gray-100 px-3 py-2.5 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick suggestions */}
      {messages.length <= 2 && !loading && (
        <div className="px-3 pb-2 flex flex-wrap gap-1">
          {["How do I post food?", "How do I claim?", "Map pins?", "Delivery?"].map(q => (
            <button
              key={q}
              onClick={() => handleQuickAsk(q)}
              className="btn-pop px-2.5 py-1 bg-green-50 text-green-700 text-[11px] rounded-full border border-green-200/60 hover:bg-green-100"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="flex items-center gap-2 px-3 py-2.5 border-t border-gray-200/50 flex-shrink-0">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={loading ? "Thinking..." : "Ask Sprout..."}
          disabled={loading}
          className="flex-1 px-3 py-2 bg-gray-50/80 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={loading}
          className="btn-pop w-9 h-9 bg-green-500 text-white rounded-xl flex items-center justify-center hover:bg-green-600 flex-shrink-0 text-sm font-bold disabled:opacity-50"
        >
          ↑
        </button>
      </form>
    </div>
  );
}
